import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, effect, ElementRef, input, signal, viewChild, OnDestroy, inject } from '@angular/core';
import { getLoggerFor, ILogger, LogInvocation, LogLevel } from "@tylertech/logging";
import { deepCopy } from "@tylertech/shared-module";
import { CategoryScaleOptions, Chart, ChartConfiguration, ChartConfigurationCustomTypesPerDataset } from "chart.js";
import "./registerChartJsElements";

let counter = 0;

@Component({
    imports: [
        CommonModule,
    ],
    selector: 'chartjs-container',
    templateUrl: `./chartJsContainer.tmpl.html`,
    styleUrls: ["./chartJsContainer.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        /* eslint-disable @typescript-eslint/naming-convention */
        "[id]": "containerId",
        "[style.display]": `"block"`,
        "[style.height]": `heightPx() ?? "100%"`,
        "[style.width]": `widthPx() ?? "100%"`,
        /* eslint-enable @typescript-eslint/naming-convention */
    },
})
export class ChartJsContainerComponent implements OnDestroy
{
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    readonly canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("chart");

    readonly config = input.required<ChartConfiguration | ChartConfigurationCustomTypesPerDataset>();
    readonly heightPx = input<number | undefined>();
    readonly widthPx = input<number | undefined>();

    readonly elementWidth = signal(0);
    readonly elementHeight = signal(0);

    readonly fallbackText = computed(() =>
    {
        const config = this.config();
        // NOTE: config is an required input.. it can't be null or undefined....
        if (!config)
        {
            return "";
        }

        let ret = `chart: ${config.options?.plugins?.title?.text} - `;
        const scales = config.options.scales;
        const indexScale = config.options.indexAxis === "y"
            ? scales?.y
            : scales?.x;

        if (indexScale && indexScale.display !== false)
        {
            const data = config.data.datasets;
            const categoryScale = indexScale.type === "category" ? indexScale as CategoryScaleOptions : config.data;

            for (let i = 0, l = categoryScale.labels?.length; i < l; i++)
            {
                ret += `${categoryScale.labels[i]}: `;

                if (data.length === 1)
                {
                    data.forEach(set =>
                    {
                        ret += `${set.data[i]}, `;
                    });
                }
                else if (data.length > 1)
                {
                    data.forEach(set =>
                    {
                        ret += `${set.label}: ${set.data[i]}, `;
                    });
                }
            }

            return ret.substring(0, ret.lastIndexOf(", "));
        }

        const data = config.data;
        if (data.datasets.length > 0)
        {
            const labels = data.labels;

            data.datasets.forEach(set =>
            {
                for (let i = 0, l = set.data.length; i < l; i++)
                {
                    const label = labels ? labels[i] : set.label;
                    ret += `${label}: ${set.data[i]}, `;
                }
            });

            return ret.substring(0, ret.lastIndexOf(", "));
        }
        return ret;
    });

    readonly height = computed(() => this.heightPx() ?? this.elementHeight());
    readonly width = computed(() => this.widthPx() ?? this.elementWidth());

    readonly containerStyles = computed<Record<string, string | null | undefined>>(() =>
    {
        const height = this.heightPx();
        const width = this.width();
        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            "height": height ? `${height}px` : `100%`,
            "max-height": height ? `${height}px` : undefined,
            "width": `100%`,
            "max-width": `${width}px`,
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    });

    private readonly log: ILogger;
    private readonly resizeObserver: ResizeObserver;
    protected chartObj?: Chart;

    readonly id: number = counter++;
    readonly containerId = `ChartJsContainerComponent_${this.id}`;

    constructor()
    {
        this.log = getLoggerFor("ChartJsContainerComponent",
            () => `ChartJsContainerComponent[${this.config()?.options?.plugins?.title?.text ?? this.containerId}]`);

        this.resizeObserver = new ResizeObserver(
            (entries, _observer) =>
            {
                entries.forEach((entry, index) =>
                {
                    const target = entry.target;
                    this.log.debug(`ResizeObserver`,
                        {
                            target,
                            targetWidth: target.clientWidth,
                            targetHeight: target.clientHeight,
                            parentWidth: target.parentElement.clientWidth,
                            parentHeight: target.parentElement.clientHeight,
                            index
                        });

                    const width = target.clientWidth;
                    if (width)
                    {
                        this.elementWidth.set(width);
                    }

                    const height = target.clientHeight;
                    if (height)
                    {
                        this.elementHeight.set(height);
                    }
                });
            });

        this.resizeObserver.observe(this.elementRef.nativeElement);

        effect(() =>
        {
            if (!this.canvasRef())
            {
                this.log.debug(`effect -> [canvasRef, config] | waiting for canvasRef`,
                    { id: this.id, canvasRef: this.canvasRef(), config: this.config() });
                return;
            }

            this.log.debug(`effect -> [canvasRef, config] | config changed`,
                { id: this.id, canvasRef: this.canvasRef(), config: this.config() });
            this.updateChart();
        });

        effect(() =>
        {
            const width = this.width();
            const height = this.height();
            this.log.debug(`Size change`, { width, height });
            this.chartObj?.resize(width, height);
        });
    }

    ngOnDestroy(): void
    {
        this.log.debug(`ngOnDestroy`, { id: this.id });

        this.resizeObserver?.disconnect();
        this.chartObj?.destroy();
    }

    @LogInvocation(LogLevel.DEBUG)
    private updateChart(): void
    {
        if (!this.config())
        {
            this.log.warning(`updateChart: no config object; BAILING `, { id: this.id, config: this.config() });
            this.chartObj?.destroy();
            return;
        }

        const config = deepCopy(this.config());

        if (config.options?.responsive)
        {
            config.options.onResize = (_: Chart, newSize: { width: number; height: number; }) =>
            {
                this.log.debug(`ChartJs | onChartResize`, {
                    newWidth: `${newSize.width}px`,
                    newHeight: `${newSize.height}px`,
                });
            };
        }

        if (config.options?.animation === undefined)
        {
            config.options.animation = false;
        }

        if (!this.chartObj)
        {
            const chartCanvas = this.canvasRef().nativeElement;
            const context = chartCanvas.getContext("2d");

            this.log.debug(`updateChart | initializing chart obj`, { context, config });
            this.chartObj = new Chart(context!, config);
        }
        else
        {
            this.chartObj.options = config.options;
            this.chartObj.data = config.data;

            this.log.debug(`updateChart | updating chart obj`, { options: config.options, data: config.data, });
            this.chartObj.update();
        }

        this.changeDetectorRef.markForCheck();
    }

    createImage(): string
    {
        const srcCanvas = this.canvasRef().nativeElement;
        const img = srcCanvas.toDataURL("image/png");
        return img.replace("data:image/png;base64,", "");
    }

    update(): void
    {
        this.chartObj.update();
    }
}
